import React, { useEffect, useMemo, useState } from 'react';
import { DateTimeField } from '@/components/DateTimeField';
import {
  EndCondition,
  formatHuman,
  formatRRule,
  MonthlyMode,
  parseRRule,
  RecurrenceState,
  Weekday,
  WEEKDAYS,
} from './rrule';

type Props = {
  value: string | null | undefined;
  onChange: (rrule: string) => void;
  disabled?: boolean;
  /// The todo's due_at. Used to surface a Feb-29 anchor warning on
  /// YEARLY rules (the underlying rule has no BYMONTH/BYMONTHDAY of
  /// its own — it inherits the anchor from DTSTART).
  anchorDate?: string | Date | null;
};

type FreqChoice = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
type PickerMode = 'easy' | 'advanced';

const MODE_STORAGE_KEY = 'recurrence.pickerMode';

const FREQ_CHOICES: Array<{ value: FreqChoice; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const WEEKDAY_LABEL: Record<Weekday, string> = {
  MO: 'M',
  TU: 'T',
  WE: 'W',
  TH: 'T',
  FR: 'F',
  SA: 'S',
  SU: 'S',
};

const UNIT_LABEL: Record<Exclude<FreqChoice, 'none'>, [string, string]> = {
  daily: ['day', 'days'],
  weekly: ['week', 'weeks'],
  monthly: ['month', 'months'],
  yearly: ['year', 'years'],
};

const MONTH_ORDINALS = [
  { value: '1', label: 'first' },
  { value: '2', label: 'second' },
  { value: '3', label: 'third' },
  { value: '4', label: 'fourth' },
  { value: '5', label: 'fifth' },
  { value: '-2', label: 'second-to-last' },
  { value: '-1', label: 'last' },
];

const WEEKDAY_NAME: Record<Weekday, string> = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
};

function freqOf(state: RecurrenceState): FreqChoice | 'custom' {
  switch (state.kind) {
    case 'none':
      return 'none';
    case 'daily':
      return 'daily';
    case 'weekly':
      return 'weekly';
    case 'monthly':
      return 'monthly';
    case 'yearly':
      return 'yearly';
    case 'custom':
      return 'custom';
  }
}

function defaultStateForFreq(freq: FreqChoice): RecurrenceState {
  switch (freq) {
    case 'none':
      return { kind: 'none' };
    case 'daily':
      return { kind: 'daily', interval: 1 };
    case 'weekly':
      return { kind: 'weekly', interval: 1, byDay: [] };
    case 'monthly':
      return { kind: 'monthly', interval: 1, mode: null };
    case 'yearly':
      return { kind: 'yearly', interval: 1 };
  }
}

// State has features only Advanced mode can edit: ordinal-weekday monthly
// rules, "last day" monthly anchor, or any UNTIL/COUNT end condition.
export function stateNeedsAdvanced(state: RecurrenceState): boolean {
  switch (state.kind) {
    case 'none':
    case 'custom':
      return false;
    case 'daily':
    case 'weekly':
    case 'yearly':
      return hasEnd(state);
    case 'monthly':
      if (hasEnd(state)) return true;
      if (!state.mode) return false;
      if (state.mode.kind === 'byWeekday') return true;
      if (state.mode.kind === 'byMonthDay' && state.mode.day === -1) return true;
      return false;
  }
}

function hasEnd(s: EndCondition): boolean {
  return !!s.until || (s.count !== undefined && s.count !== null);
}

// Inline warning text when a monthly rule anchors on day 29-31. Months
// shorter than the picked day are SKIPPED by RFC 5545; we surface this
// so the user knows "every 31st" doesn't fire in February/etc. The
// "last day" option (BYMONTHDAY=-1) is the safe end-of-month anchor.
function monthlyAnchorWarning(
  state: Extract<RecurrenceState, { kind: 'monthly' }>,
): string | null {
  if (!state.mode || state.mode.kind !== 'byMonthDay') return null;
  const d = state.mode.day;
  if (d >= 29 && d <= 31) {
    return "Months without this day will skip. Use 'Last day' for end-of-month.";
  }
  return null;
}

// Yearly rules have no BYMONTH/BYMONTHDAY in our model — the anchor
// comes from the todo's due_at (DTSTART). When that anchor is Feb 29
// the rule only fires in leap years; warn the user.
function yearlyAnchorWarning(anchor: string | Date | null | undefined): string | null {
  if (!anchor) return null;
  const d = anchor instanceof Date ? anchor : new Date(anchor);
  if (isNaN(d.getTime())) return null;
  if (d.getMonth() === 1 && d.getDate() === 29) {
    return 'February 29 only exists in leap years. Non-leap years will skip.';
  }
  return null;
}

function loadMode(): PickerMode {
  try {
    const v = window.localStorage.getItem(MODE_STORAGE_KEY);
    return v === 'advanced' ? 'advanced' : 'easy';
  } catch {
    return 'easy';
  }
}

function saveMode(mode: PickerMode) {
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function RecurrencePicker({ value, onChange, disabled, anchorDate }: Props) {
  const state = useMemo(() => parseRRule(value), [value]);
  const freq = freqOf(state);

  const [mode, setMode] = useState<PickerMode>(() => {
    const persisted = loadMode();
    return stateNeedsAdvanced(state) ? 'advanced' : persisted;
  });

  // If the state mutates into something Easy can't represent (e.g. user
  // toggles to "On the second Tuesday"), force Advanced so the controls
  // that produced it stay visible.
  useEffect(() => {
    if (stateNeedsAdvanced(state) && mode !== 'advanced') {
      setMode('advanced');
    }
  }, [state, mode]);

  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as PickerMode;
    setMode(next);
    saveMode(next);
  };

  const emit = (next: RecurrenceState) => onChange(formatRRule(next));

  const handleFreqChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as FreqChoice;
    emit(defaultStateForFreq(next));
  };

  // When state is 'custom', show the human label and disable edits.
  if (state.kind === 'custom') {
    return (
      <div className="recurrence-picker recurrence-picker--custom">
        <select
          className="todo-detail__select"
          value="custom"
          disabled={disabled}
          onChange={handleFreqChange}
          aria-label="Recurrence"
        >
          <option value="custom" disabled>
            Custom
          </option>
          {FREQ_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <span
          className="recurrence-picker__custom-raw"
          title={state.raw}
          aria-label={`Custom rule: ${state.raw}`}
        >
          {formatHuman(state)}
        </span>
      </div>
    );
  }

  const isAdvanced = mode === 'advanced';

  return (
    <div className="recurrence-picker">
      <select
        className="todo-detail__select recurrence-picker__mode"
        value={mode}
        disabled={disabled}
        onChange={handleModeChange}
        aria-label="Editor mode"
      >
        <option value="easy">Easy</option>
        <option value="advanced">Advanced</option>
      </select>

      {isAdvanced && state.kind !== 'none' && (
        <div
          className="recurrence-picker__preview"
          aria-label="Preview"
          aria-live="polite"
        >
          {formatHuman(state)}
        </div>
      )}

      <select
        className="todo-detail__select"
        value={freq}
        disabled={disabled}
        onChange={handleFreqChange}
        aria-label="Recurrence"
      >
        {FREQ_CHOICES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      {state.kind !== 'none' && (
        <IntervalField state={state} onChange={emit} disabled={disabled} />
      )}

      {state.kind === 'weekly' && (
        <WeekdayChips state={state} onChange={emit} disabled={disabled} />
      )}

      {state.kind === 'monthly' && (
        <MonthlyModeField
          state={state}
          onChange={emit}
          disabled={disabled}
          advanced={isAdvanced}
        />
      )}

      {isAdvanced && state.kind === 'monthly' && monthlyAnchorWarning(state) && (
        <p className="recurrence-picker__warning" role="note">
          {monthlyAnchorWarning(state)}
        </p>
      )}

      {isAdvanced && state.kind === 'yearly' && yearlyAnchorWarning(anchorDate) && (
        <p className="recurrence-picker__warning" role="note">
          {yearlyAnchorWarning(anchorDate)}
        </p>
      )}

      {isAdvanced && state.kind !== 'none' && (
        <EndConditionField state={state} onChange={emit} disabled={disabled} />
      )}
    </div>
  );
}

// ---------- sub-components ----------

type IntervalFieldProps = {
  state: Extract<RecurrenceState, { interval: number }>;
  onChange: (s: RecurrenceState) => void;
  disabled?: boolean;
};

function IntervalField({ state, onChange, disabled }: IntervalFieldProps) {
  const choice = state.kind as Exclude<FreqChoice, 'none'>;
  const [one, many] = UNIT_LABEL[choice];
  const unit = state.interval === 1 ? one : many;
  return (
    <span className="recurrence-picker__interval">
      every{' '}
      <input
        type="number"
        min={1}
        max={99}
        className="recurrence-picker__interval-input"
        value={state.interval}
        disabled={disabled}
        aria-label="Interval"
        onChange={(e) => {
          const n = Math.max(1, Math.min(99, Number(e.target.value) || 1));
          onChange({ ...state, interval: n });
        }}
      />{' '}
      {unit}
    </span>
  );
}

type WeekdayChipsProps = {
  state: Extract<RecurrenceState, { kind: 'weekly' }>;
  onChange: (s: RecurrenceState) => void;
  disabled?: boolean;
};

function WeekdayChips({ state, onChange, disabled }: WeekdayChipsProps) {
  const selected = new Set<Weekday>(state.byDay);
  return (
    <div className="recurrence-picker__weekday-row" role="group" aria-label="Weekdays">
      {WEEKDAYS.map((d) => {
        const isOn = selected.has(d);
        return (
          <button
            key={d}
            type="button"
            className={
              'recurrence-picker__weekday-chip' +
              (isOn ? ' recurrence-picker__weekday-chip--active' : '')
            }
            disabled={disabled}
            aria-pressed={isOn}
            aria-label={WEEKDAY_NAME[d]}
            onClick={() => {
              const next = new Set(selected);
              if (isOn) next.delete(d);
              else next.add(d);
              const byDay = WEEKDAYS.filter((w) => next.has(w));
              onChange({ ...state, byDay });
            }}
          >
            {WEEKDAY_LABEL[d]}
          </button>
        );
      })}
    </div>
  );
}

type MonthlyModeFieldProps = {
  state: Extract<RecurrenceState, { kind: 'monthly' }>;
  onChange: (s: RecurrenceState) => void;
  disabled?: boolean;
  advanced: boolean;
};

function MonthlyModeField({
  state,
  onChange,
  disabled,
  advanced,
}: MonthlyModeFieldProps) {
  // A monthly rule with no BYMONTHDAY/BYDAY renders as a third "generic" radio
  // ("same day each month"), implemented by mode === null.
  const mode: 'none' | 'day' | 'weekday' =
    state.mode === null
      ? 'none'
      : state.mode.kind === 'byMonthDay'
        ? 'day'
        : 'weekday';

  const setMode = (next: 'none' | 'day' | 'weekday') => {
    let nextMode: MonthlyMode | null;
    switch (next) {
      case 'none':
        nextMode = null;
        break;
      case 'day':
        nextMode = {
          kind: 'byMonthDay',
          day: state.mode?.kind === 'byMonthDay' ? state.mode.day : 1,
        };
        break;
      case 'weekday':
        nextMode =
          state.mode?.kind === 'byWeekday'
            ? state.mode
            : { kind: 'byWeekday', ordinal: 1, day: 'MO' };
        break;
    }
    onChange({ ...state, mode: nextMode });
  };

  return (
    <div className="recurrence-picker__monthly">
      <label className="recurrence-picker__radio">
        <input
          type="radio"
          name="monthly-mode"
          checked={mode === 'none'}
          disabled={disabled}
          onChange={() => setMode('none')}
        />
        <span>Same day each month</span>
      </label>

      <label className="recurrence-picker__radio">
        <input
          type="radio"
          name="monthly-mode"
          checked={mode === 'day'}
          disabled={disabled}
          onChange={() => setMode('day')}
        />
        <span>On day</span>
        <select
          className="recurrence-picker__inline-select"
          value={
            state.mode?.kind === 'byMonthDay' ? String(state.mode.day) : '1'
          }
          disabled={disabled || mode !== 'day'}
          aria-label="Day of month"
          onChange={(e) => {
            const day = Number(e.target.value);
            onChange({
              ...state,
              mode: { kind: 'byMonthDay', day },
            });
          }}
        >
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          {advanced && <option value={-1}>last</option>}
        </select>
      </label>

      {advanced && (
        <label className="recurrence-picker__radio">
          <input
            type="radio"
            name="monthly-mode"
            checked={mode === 'weekday'}
            disabled={disabled}
            onChange={() => setMode('weekday')}
          />
          <span>On the</span>
          <select
            className="recurrence-picker__inline-select"
            value={
              state.mode?.kind === 'byWeekday' ? String(state.mode.ordinal) : '1'
            }
            disabled={disabled || mode !== 'weekday'}
            aria-label="Ordinal"
            onChange={(e) => {
              const ordinal = Number(e.target.value);
              const day =
                state.mode?.kind === 'byWeekday' ? state.mode.day : 'MO';
              onChange({
                ...state,
                mode: { kind: 'byWeekday', ordinal, day },
              });
            }}
          >
            {MONTH_ORDINALS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="recurrence-picker__inline-select"
            value={state.mode?.kind === 'byWeekday' ? state.mode.day : 'MO'}
            disabled={disabled || mode !== 'weekday'}
            aria-label="Weekday"
            onChange={(e) => {
              const day = e.target.value as Weekday;
              const ordinal =
                state.mode?.kind === 'byWeekday' ? state.mode.ordinal : 1;
              onChange({
                ...state,
                mode: { kind: 'byWeekday', ordinal, day },
              });
            }}
          >
            {WEEKDAYS.map((d) => (
              <option key={d} value={d}>
                {WEEKDAY_NAME[d]}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

// ---------- End condition (Advanced only) ----------

type EndKind = 'never' | 'on' | 'after';

type EndConditionFieldProps = {
  state: Exclude<RecurrenceState, { kind: 'none' } | { kind: 'custom' }>;
  onChange: (s: RecurrenceState) => void;
  disabled?: boolean;
};

function endKindOf(s: EndCondition): EndKind {
  if (s.until) return 'on';
  if (s.count !== undefined && s.count !== null) return 'after';
  return 'never';
}

// Format Date for an <input type="date"> (YYYY-MM-DD, UTC components so
// the round-trip matches the underlying UNTIL semantics).
function toDateInputValue(d: Date | null | undefined): string {
  if (!d) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromDateInputValue(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  return new Date(Date.UTC(y, mo - 1, day, 23, 59, 59));
}

function EndConditionField({
  state,
  onChange,
  disabled,
}: EndConditionFieldProps) {
  const kind = endKindOf(state);

  const setKind = (next: EndKind) => {
    if (next === 'never') {
      onChange({ ...state, until: null, count: null });
    } else if (next === 'on') {
      const until =
        state.until ?? new Date(Date.UTC(new Date().getUTCFullYear() + 1, 0, 1, 23, 59, 59));
      onChange({ ...state, until, count: null });
    } else {
      const count = state.count ?? 10;
      onChange({ ...state, count, until: null });
    }
  };

  return (
    <fieldset className="recurrence-picker__end">
      <legend className="recurrence-picker__end-legend">Ends</legend>
      <label className="recurrence-picker__radio">
        <input
          type="radio"
          name="end-kind"
          checked={kind === 'never'}
          disabled={disabled}
          onChange={() => setKind('never')}
        />
        <span>Never</span>
      </label>
      <label className="recurrence-picker__radio">
        <input
          type="radio"
          name="end-kind"
          checked={kind === 'on'}
          disabled={disabled}
          onChange={() => setKind('on')}
        />
        <span>On</span>
        <DateTimeField
          mode="date"
          className="datetime-field--inline"
          value={toDateInputValue(state.until)}
          disabled={disabled || kind !== 'on'}
          ariaLabel="End date"
          onChange={(v) => {
            const d = fromDateInputValue(v);
            if (d) onChange({ ...state, until: d, count: null });
          }}
        />
      </label>
      <label className="recurrence-picker__radio">
        <input
          type="radio"
          name="end-kind"
          checked={kind === 'after'}
          disabled={disabled}
          onChange={() => setKind('after')}
        />
        <span>After</span>
        <input
          type="number"
          min={1}
          max={999}
          className="recurrence-picker__interval-input"
          value={state.count ?? ''}
          disabled={disabled || kind !== 'after'}
          aria-label="Occurrences"
          onChange={(e) => {
            const n = Math.max(1, Math.min(999, Number(e.target.value) || 1));
            onChange({ ...state, count: n, until: null });
          }}
        />
        <span>occurrences</span>
      </label>
    </fieldset>
  );
}
